import React from "react";
import Icon from "./Icon";

/**
 * The handset at the centre of the hero, drawn rather than photographed.
 *
 * The marketing renders in /Images are composites on their own purple
 * backgrounds, so none of them can be dropped into a frame — and a screenshot
 * would go soft the moment the hero scales it up. Building the screen out of
 * the same icon set and brand colours the rest of the site uses keeps it sharp
 * at any size, lets the world switcher change what is on screen, and costs no
 * request.
 *
 * Sizing is fixed at 300×620 on purpose: everything inside is laid out against
 * those numbers, and the hero scales the whole thing as one object instead of
 * asking two dozen paddings to be fluid.
 */

const TAB_ICONS = ["home", "market", "sparkle", "gifts", "users"];

function StatusBar() {
  return (
    <div className="flex items-center justify-between px-5 pt-3 text-[10px] font-semibold text-white/90">
      <span>9:41</span>
      <div className="flex items-center gap-1" aria-hidden="true">
        {/* Signal staircase, battery, then the battery cap. */}
        <span className="flex items-end gap-[2px]">
          {[4, 6, 8, 10].map((h) => (
            <span
              key={h}
              className="w-[2px] rounded-sm bg-white/85"
              style={{ height: `${h}px` }}
            />
          ))}
        </span>
        <span className="ml-1 flex h-[9px] w-[17px] items-center rounded-[3px] border border-white/70 p-[1.5px]">
          <span className="h-full w-[70%] rounded-[1px] bg-white/85" />
        </span>
      </div>
    </div>
  );
}

function PhoneMock({ world }) {
  const { screen, accent } = world;

  return (
    <div className="relative h-[620px] w-[300px]">
      {/* --- Chassis ------------------------------------------------------
          The metal edge is a gradient rather than a flat colour, so the turn
          towards the pointer catches light down one side. */}
      <div className="relative h-full w-full rounded-[2.75rem] bg-[linear-gradient(145deg,#4A4A55_0%,#22222A_38%,#101014_72%,#33333D_100%)] p-[9px] shadow-[0_60px_90px_-40px_rgba(0,0,0,0.9),0_0_0_1px_rgba(255,255,255,0.09)]">
        <div className="mw-phone-screen h-full w-full rounded-[2.15rem] bg-[#FAF8F2]">
          {/* --- App bar --- */}
          <div className="relative bg-[linear-gradient(150deg,#7B1BC4_0%,#6A0DAD_48%,#4C087D_100%)] pb-4">
            <StatusBar />

            <div className="mt-3 flex items-center justify-between px-5">
              <span className="flex flex-col gap-[3px]" aria-hidden="true">
                {[11, 8, 11].map((w, i) => (
                  <span
                    key={i}
                    className="h-[1.5px] rounded-full bg-white/85"
                    style={{ width: `${w}px` }}
                  />
                ))}
              </span>
              <span className="text-[12px] font-bold tracking-[0.26em] text-white">
                MONDOWAY
              </span>
              <Icon name="search" className="h-[15px] w-[15px] text-white/85" />
            </div>

            <div className="mt-4 flex items-center gap-2 px-5">
              <span className="flex h-[30px] flex-1 items-center gap-2 rounded-full bg-white/15 px-3 text-[10px] text-white/70 ring-1 ring-white/20 backdrop-blur">
                <Icon name="search" className="h-3 w-3" />
                {screen.searchHint}
              </span>
              <span className="flex h-[30px] w-[30px] items-center justify-center rounded-full bg-white/15 text-white ring-1 ring-white/20">
                <Icon name="sliders" className="h-[13px] w-[13px]" />
              </span>
            </div>

            <div className="mt-3 flex items-center gap-1.5 px-5">
              <Icon name="pin" className="h-3 w-3 text-[#E9D5FF]" />
              <span className="text-[10px] font-medium text-[#E9D5FF]">
                {screen.location}
              </span>
            </div>
          </div>

          {/* --- Body --- */}
          <div className="px-5 pt-4">
            <div className="flex items-baseline justify-between">
              <span className="text-[11px] font-bold text-[#1E1B16]">
                {screen.heading}
              </span>
              <span className="text-[9px] font-semibold text-[#6A0DAD]">
                See all
              </span>
            </div>

            <div className="mt-3 grid grid-cols-3 gap-2">
              {screen.tiles.map((tile) => (
                <div
                  key={tile.label}
                  className="flex flex-col items-center gap-1.5 rounded-2xl border border-[#EFE6F9] bg-white py-2.5 shadow-[0_6px_14px_-10px_rgba(30,27,22,0.5)]"
                >
                  <span
                    className="flex h-8 w-8 items-center justify-center rounded-xl text-[#6A0DAD]"
                    style={{ background: accent.tile }}
                  >
                    <Icon name={tile.icon} className="h-[17px] w-[17px]" />
                  </span>
                  <span className="text-[8px] font-semibold leading-none text-[#4D4B46]">
                    {tile.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Promo strip — the one place the phone screen carries the
                accent of the world that is currently selected. */}
            <div
              className="relative mt-4 overflow-hidden rounded-2xl p-4 text-white"
              style={{ background: accent.promo }}
            >
              <div
                className="mw-grain absolute inset-0 opacity-[0.16] mix-blend-overlay"
                aria-hidden="true"
              />
              <span className="relative block text-[13px] font-bold leading-tight">
                {screen.promoTitle}
              </span>
              <span className="relative mt-1 block text-[9px] leading-snug text-white/80">
                {screen.promoSub}
              </span>
              <span className="relative mt-3 inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-[9px] font-bold text-[#3B0764]">
                {screen.promoCta}
                <Icon name="arrow" className="h-2.5 w-2.5" />
              </span>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <span className="text-[11px] font-bold text-[#1E1B16]">
                {screen.listHeading}
              </span>
              <span className="text-[9px] font-semibold text-[#6A0DAD]">
                See all
              </span>
            </div>

            <div className="mt-2.5 space-y-2">
              {screen.rows.map((row) => (
                <div
                  key={row.title}
                  className="flex items-center gap-2.5 rounded-xl border border-[#F0EAE1] bg-white p-2"
                >
                  <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[#6A0DAD]"
                    style={{ background: accent.tile }}
                  >
                    <Icon name={row.icon} className="h-[15px] w-[15px]" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[9.5px] font-semibold text-[#1E1B16]">
                      {row.title}
                    </span>
                    <span className="block truncate text-[8px] text-[#7A6F61]">
                      {row.meta}
                    </span>
                  </span>
                  <span className="shrink-0 rounded-full bg-[#F4E8FF] px-2 py-[3px] text-[8px] font-bold text-[#6A0DAD]">
                    {row.tag}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* --- Tab bar --- */}
          {/* The screen is transformed, which makes it the containing block
              here — so the bar anchors to the glass, not to the chassis. */}
          <div className="absolute inset-x-0 bottom-0 flex items-end justify-around rounded-b-[2.15rem] border-t border-[#EFE6F9] bg-white/95 px-4 pb-4 pt-2.5 backdrop-blur">
            {TAB_ICONS.map((name, i) =>
              i === 2 ? (
                <span
                  key={name}
                  className="-mt-6 flex h-11 w-11 items-center justify-center rounded-full bg-[linear-gradient(145deg,#7B1BC4,#4C087D)] text-white shadow-[0_12px_22px_-8px_rgba(106,13,173,0.9)] ring-4 ring-white"
                >
                  <Icon name={name} className="h-[18px] w-[18px]" />
                </span>
              ) : (
                <span
                  key={name}
                  className={i === 0 ? "text-[#6A0DAD]" : "text-[#C3BBAF]"}
                >
                  <Icon name={name} className="h-[17px] w-[17px]" />
                </span>
              )
            )}
          </div>
        </div>
      </div>

      {/* Notch and side buttons sit over the chassis, outside the clipping
          screen — anything inside it would be cut by the rounded corners. */}
      <span
        className="absolute left-1/2 top-[13px] h-[22px] w-[92px] -translate-x-1/2 rounded-full bg-[#0C0C10]"
        aria-hidden="true"
      >
        <span className="absolute right-4 top-1/2 h-[7px] w-[7px] -translate-y-1/2 rounded-full bg-[#1D1D28] ring-1 ring-white/10" />
      </span>
      <span
        className="absolute -right-[2px] top-[150px] h-[64px] w-[3px] rounded-r-full bg-[linear-gradient(180deg,#4A4A55,#22222A)]"
        aria-hidden="true"
      />
      <span
        className="absolute -left-[2px] top-[128px] h-[38px] w-[3px] rounded-l-full bg-[linear-gradient(180deg,#4A4A55,#22222A)]"
        aria-hidden="true"
      />
      <span
        className="absolute -left-[2px] top-[176px] h-[38px] w-[3px] rounded-l-full bg-[linear-gradient(180deg,#4A4A55,#22222A)]"
        aria-hidden="true"
      />
    </div>
  );
}

export default PhoneMock;
